"use client";

import { useSyncExternalStore } from "react";

import { isShellUserAgent } from "./shell";

// Whether we're in the native shell is fixed for the life of the document,
// so there's nothing to subscribe to; the server snapshot is "not the
// shell", which keeps SSR (including force-static pages) shell-agnostic and
// hydration-safe.
const noSubscribe = () => () => {};

// Memoized because React calls getSnapshot during render AND again in its
// tearing check, for every consumer, on every navigation — and the UA can't
// change. Caching also keeps the returned value reference-stable.
let cached: boolean | undefined;
const readIsShell = () => (cached ??= isShellUserAgent(navigator.userAgent));

/** Client-side shell detection. Server components use `isShellRequest()`
 *  (lib/shell-server.ts) instead — this is for components that must decide
 *  without a server round-trip, or that render on force-static pages. */
function useIsShell(): boolean {
  return useSyncExternalStore(noSubscribe, readIsShell, () => false);
}

/** Merge a server-resolved shell flag with client detection.
 *
 *  The precedence rule — the server wins when it knew, the client detects
 *  otherwise — is what keeps SSR from painting the web affordance and then
 *  swapping after hydration. It lived in two components with two different
 *  local namings; one home so it can't drift.
 *
 *  The hook underneath is called unconditionally (rules of hooks), so this
 *  is safe to call with `undefined` on the mounts that have no server flag
 *  (`/legal/*` is force-static and outside (authed)). */
export function useShellFlag(shell?: boolean): boolean {
  const detected = useIsShell();
  return shell ?? detected;
}
